import React, { createContext, useContext, useEffect, useState } from 'react';

import { getToken, getPointsByUser, getPurposeByUser } from '../shared/src/services/user-info.service';
import { getUserState } from '../services/validationServices';
import { getProfileByUser, getFinancialProductsByUser } from '../services/profile/profileServices';
import { getPrincipalResults, getSecondaryResults } from '../services/profile/questionServices';
import { saveWaitingList, saveDiscountCode } from '../services/servicesRoute/servicesRouteServices';

interface AppDataContextType {
  userState: any;
  userStateLoading: boolean;
  userStateError: any;
  profile: any;
  profileLoading: boolean;
  profileError: any;
  principalResults: any;
  principalResultsLoading: boolean;
  principalResultsError: any;
  secondaryResults: any;
  secondaryResultsLoading: boolean;
  secondaryResultsError: any;
  financialProducts: any;
  financialProductsLoading: boolean;
  financialProductsError: any;
  saveWaitingList: () => Promise<any>;
  saveWaitingListLoading: boolean;
  saveWaitingListError: any;
  saveDiscountCode: (discountCode: string) => Promise<any>;
  saveDiscountCodeLoading: boolean;
  saveDiscountCodeError: any;

  // User-info.service
  points: number | null;
  pointsLoading: boolean;
  pointsError: any;
  purpose: any;
  purposeLoading: boolean;
  purposeError: any;
  getToken: (uid: string) => Promise<string | undefined>;
  getCurrentStateByUser: () => Promise<any>;

  // Nuevo estado expuesto
  principalPurposeName: string | null;

  // Global error expuesto
  globalError: string | null;
}

export const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export const AppDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Estados para cada petición de datos
  const [userState, setUserState] = useState<any>(null);
  const [userStateLoading, setUserStateLoading] = useState(true);
  const [userStateError, setUserStateError] = useState<any>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const [profile, setProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<any>(null);

  const [principalResults, setPrincipalResults] = useState<any>(null);
  const [principalResultsLoading, setPrincipalResultsLoading] = useState(true);
  const [principalResultsError, setPrincipalResultsError] = useState<any>(null);

  const [secondaryResults, setSecondaryResults] = useState<any>(null);
  const [secondaryResultsLoading, setSecondaryResultsLoading] = useState(true);
  const [secondaryResultsError, setSecondaryResultsError] = useState<any>(null);

  const [financialProducts, setFinancialProducts] = useState<any>(null);
  const [financialProductsLoading, setFinancialProductsLoading] = useState(true);
  const [financialProductsError, setFinancialProductsError] = useState<any>(null);

  // Estados para user-info.service
  const [points, setPoints] = useState<number | null>(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsError, setPointsError] = useState<any>(null);
  const [purpose, setPurpose] = useState<any>(null);
  const [purposeLoading, setPurposeLoading] = useState(false);
  const [purposeError, setPurposeError] = useState<any>(null);
  // Estados para acciones (mutaciones)
  const [saveWaitingListLoading, setSaveWaitingListLoading] = useState(false);
  const [saveWaitingListError, setSaveWaitingListError] = useState<any>(null);
  const [saveDiscountCodeLoading, setSaveDiscountCodeLoading] = useState(false);
  const [saveDiscountCodeError, setSaveDiscountCodeError] = useState<any>(null);

  // Estado para el nombre del propósito principal
  const [principalPurposeName, setPrincipalPurposeName] = useState<string | null>(null);

  // Acciones expuestas
  const saveWaitingListAction = async () => {
    setSaveWaitingListLoading(true);
    setSaveWaitingListError(null);
    try {
      const res = await saveWaitingList();
      return res;
    } catch (err) {
      setSaveWaitingListError(err);
      setGlobalError('Error al guardar en la lista de espera');
      throw err;
    } finally {
      setSaveWaitingListLoading(false);
    }
  };

  const saveDiscountCodeAction = async (discountCode: string) => {
    setSaveDiscountCodeLoading(true);
    setSaveDiscountCodeError(null);
    try {
      const res = await saveDiscountCode(discountCode);
      return res;
    } catch (err) {
      setSaveDiscountCodeError(err);
      setGlobalError('Error al guardar el código de descuento');
      throw err;
    } finally {
      setSaveDiscountCodeLoading(false);
    }
  };


  useEffect(() => {
    // Obtener el token antes de las demás peticiones
    let isMounted = true;
    const fetchAll = async () => {
      try {
        // Puedes cambiar el UID por el valor real que corresponda en tu app
        const uid = sessionStorage.getItem('mentoria-uid') || '';
        let token;
        try {
          token = await getToken(uid);
          if (token && isMounted) {
            sessionStorage.setItem('mentoria-jwt', token);
          }
        } catch (err) {
          setGlobalError('Error al obtener el token');
        }

        // Petición: getUserState
        setUserStateLoading(true);
        getUserState()
          .then(res => { if (isMounted) setUserState(res?.data) })
          .catch(err => { if (isMounted) { setUserStateError(err); setGlobalError('Error al obtener el estado de usuario'); } })
          .finally(() => { if (isMounted) setUserStateLoading(false) });

        // Petición: getProfileByUser
        setProfileLoading(true);
        getProfileByUser()
          .then(res => {
            if (isMounted) setProfile(res?.data);
            // Validar y poblar medallas y challenges si no existen
            if (isMounted && res?.data) {
              if (!res?.data?.data?.medals) res.data.data.medals = [];
              if (!res?.data?.data?.challenges) res.data.data.challenges = [];
            }
          })
          .catch(err => { if (isMounted) { setProfileError(err); setGlobalError('Error al obtener el perfil'); } })
          .finally(() => { if (isMounted) setProfileLoading(false) });

        // Petición: getPrincipalResults
        setPrincipalResultsLoading(true);
        getPrincipalResults()
          .then(res => { if (isMounted) setPrincipalResults(res?.data) })
          .catch(err => { if (isMounted) { setPrincipalResultsError(err); setGlobalError('Error al obtener resultados principales'); } })
          .finally(() => { if (isMounted) setPrincipalResultsLoading(false) });

        // Petición: getSecondaryResults
        setSecondaryResultsLoading(true);
        getSecondaryResults()
          .then(res => { if (isMounted) setSecondaryResults(res?.data) })
          .catch(err => { if (isMounted) { setSecondaryResultsError(err); setGlobalError('Error al obtener resultados secundarios'); } })
          .finally(() => { if (isMounted) setSecondaryResultsLoading(false) });

        // Petición: getFinancialProductsByUser
        setFinancialProductsLoading(true);
        getFinancialProductsByUser()
          .then(res => { if (isMounted) setFinancialProducts(res?.data) })
          .catch(err => { if (isMounted) { setFinancialProductsError(err); setGlobalError('Error al obtener productos financieros'); } })
          .finally(() => { if (isMounted) setFinancialProductsLoading(false) });

        // Petición: getPointsByUser
        setPointsLoading(true);
        getPointsByUser()
          .then(res => {
            if (isMounted) setPoints(res?.data?.data ?? null);
            // Actualiza storage
            try {
              sessionStorage.setItem('mentoria-points', JSON.stringify(res?.data?.data ?? null));
            } catch {}
          })
          .catch(err => { if (isMounted) { setPointsError(err); setGlobalError('Error al obtener puntos'); } })
          .finally(() => { if (isMounted) setPointsLoading(false) });

        // Petición: getPurposeByUser
        setPurposeLoading(true);
        getPurposeByUser()
          .then(res => {
            if (isMounted) setPurpose(res?.data);
            // Guardar el nombre del propósito si existe
            if (res?.data && res?.data?.data?.name) {
              setPrincipalPurposeName(res?.data?.data?.name);
            }
          })
          .catch(err => { if (isMounted) { setPurposeError(err); setGlobalError('Error al obtener propósito'); } })
          .finally(() => { if (isMounted) setPurposeLoading(false) });
      } catch (e) {
        setGlobalError('Error general al cargar los datos');
      }
    };
    fetchAll();
    return () => { isMounted = false; };
  }, []);

  // Implementación por defecto para getCurrentStateByUser
  const getCurrentStateByUser = async () => {
    // Puedes personalizar esta función según la lógica real
    return userState;
  };

  return (
    <AppDataContext.Provider
      value={{
        userState,
        userStateLoading,
        userStateError,
        profile,
        profileLoading,
        profileError,
        principalResults,
        principalResultsLoading,
        principalResultsError,
        secondaryResults,
        secondaryResultsLoading,
        secondaryResultsError,
        financialProducts,
        financialProductsLoading,
        financialProductsError,
        saveWaitingList: saveWaitingListAction,
        saveWaitingListLoading,
        saveWaitingListError,
        saveDiscountCode: saveDiscountCodeAction,
        saveDiscountCodeLoading,
        saveDiscountCodeError,

        // user-info.service
        points,
        pointsLoading,
        pointsError,
        purpose,
        purposeLoading,
        purposeError,
        getToken,
        getCurrentStateByUser,

        // Nuevo estado expuesto
        principalPurposeName,
        globalError,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};

export const useAppData = () => {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData debe usarse dentro de AppDataProvider');
  return ctx;
};
